"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { signIn as loginRequest } from "@/modules/auth/authService";
import { useAuth } from "@/modules/auth/context/UserContext";
import { FaUser, FaLock } from "react-icons/fa";
import { setMfaSession } from "@/modules/auth/mfaSession";
import { config } from "@/config/env";

export default function LoginPage() {
    const router = useRouter();
    const { signIn, isAuthenticated, isLoading: authIsLoading, hydrated } = useAuth();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const showSso = config.SHOW_SSO;

    useEffect(() => {
        if (!hydrated || authIsLoading) return;
        if (isAuthenticated) {
            router.replace("/dashboard");
        }
    }, [hydrated, authIsLoading, isAuthenticated, router]);

    if (!hydrated || authIsLoading || isAuthenticated) {
        return null;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        console.log("[LOGIN] Attempting sign in with:", username);

        try {
            const result = await loginRequest(username, password);

            if ("requiresTwoFactor" in result) {
                console.log("[LOGIN] 2FA required, redirecting to verification page");
                setMfaSession({
                    mfaSessionToken: result.mfaSessionToken,
                    username,
                    password,
                    twoFactorMethods: result.twoFactorMethods,
                    userId: result.userId,
                    hints: result.hints,
                });
                router.push("/two-factor-auth");
                return;
            }

            const { accessToken, mapToken, authData, profileData, userPreferences } = result;

            console.log("[LOGIN] Saving to AuthContext:", {
                userId: authData.userId,
                userName: authData.userName,
                roles: authData.roles,
                profileData,
                userPreferences,
            });

            signIn(accessToken, {
                userId: authData.userId,
                userName: authData.userName,
                roles: authData.roles,
            }, mapToken, profileData, userPreferences);

            router.push("/dashboard");
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : "Login failed";
            console.error("[LOGIN] Error during signIn:", err);
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSsoLogin = () => {
        const state = Math.random().toString(36).substring(2) + Date.now().toString(36);
        sessionStorage.setItem("sso_state", state);

        const params = new URLSearchParams({
            client_id: config.MICROSOFT_SSO_CLIENT_ID,
            response_type: "code",
            redirect_uri: `${window.location.origin}/login/callback/`,
            scope: "https://graph.microsoft.com/user.read",
            state,
        });

        window.location.href = `https://login.microsoftonline.com/${config.MICROSOFT_SSO_TENANT_ID}/oauth2/v2.0/authorize?${params.toString()}`;
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-[#2d3a3f] font-roboto relative">
            <div
                className="fixed inset-0 pointer-events-none opacity-30"
                style={{
                    backgroundImage: `url('/images/l2t_logo_bg.png')`,
                    backgroundPosition: "right bottom",
                    backgroundSize: "500px auto",
                    backgroundRepeat: "no-repeat",
                }}
            />
            <div className="max-w-6xl mx-auto text-center relative z-10">
                <div className="bg-white/10 backdrop-blur-sm rounded-[4rem] shadow-2xl p-20">
                    <div className="flex justify-center mb-8">
                        <Image src="/l2t-media-logo.png" alt="L2T Logo" width={250} height={167} className="mx-auto" unoptimized />
                    </div>

                    <h1 className="text-center text-white font-normal text-2xl mb-6">Welcome to the L2T Dashboard!</h1>

                    <form onSubmit={handleSubmit} className="text-white">
                    <div className="relative">
                        <span className="absolute left-3 top-3 text-gray-500">
                            <FaUser className="text-[#5a717a]" />
                        </span>
                        <input
                            type="text"
                            placeholder="Username"
                            className="w-full bg-white text-black py-2 pl-10 pr-3 text-base focus:outline-none"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                    </div>

                    <div className="relative mt-2">
                        <span className="absolute left-3 top-3 text-gray-500">
                            <FaLock className="text-[#5a717a]" />
                        </span>
                        <input
                            type="password"
                            placeholder="Password"
                            className="w-full bg-white text-black py-2 pl-10 pr-3 text-base focus:outline-none"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full flex justify-center items-center gap-2 bg-white text-[#2d3a3f] font-semibold py-3 text-base mt-3 rounded-lg border border-[#d3e2e8] transition-all duration-200 hover:shadow-[0_0_30px_15px_rgba(116,196,198,0.6)] ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                            }`}
                    >
                        {isLoading ? (
                            <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                            </svg>
                        ) : (
                            "Sign In"
                        )}
                    </button>

                    <div className="flex items-center justify-between mt-2 text-base">
                        <label className="flex items-center gap-2 text-gray-300">
                            <input type="checkbox" className="accent-[#74c4c6]" />
                            Remember Me
                        </label>
                        <a href="/forgot-password" className="text-gray-300 underline hover:text-white">
                            Forgot Password?
                        </a>
                    </div>

                    {error && <p className="text-white text-base text-center mt-2">{error}</p>}
                    <div id="sso-button-container" className="mt-4 text-center" style={{ display: showSso ? "block" : "none" }}>
                        <button
                            type="button"
                            onClick={handleSsoLogin}
                            className="text-gray-400 text-xs hover:text-gray-200 transition-colors underline"
                        >
                            L2T Internal SSO
                        </button>
                    </div>
                </form>
                </div>
            </div>
        </div>
    );
}
